import { ColumnHelper } from "@tanstack/react-table";
import find from "lodash/find";
import includes from "lodash/includes";
import map from "lodash/map";
import { useTableSorting } from "PFApp/hooks/table";
import { Stack } from "PFComponents/containers";
import Table from "PFComponents/table";
import { Tile } from "PFComponents/tile";
import i18n from "PFCore/i18n";
import NoProfilesIllustration from "PFTheme/graphics/illustrations/illustration-no-profiles.svg";
import { TableColumn } from "PFTypes";
import { OrderOption } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { RoleWatcher } from "../../../../../types/watchers";
import { useCellFactory } from "./hooks/use_cell_factory";
import { useInterestedContext } from "./interested_context";
import css from "./interested_table.module.scss";

const COLUMNS_SIZE = {
  1: 100,
  2: 180,
  3: 250
};

const getColumnConfig = (column: TableColumn, orderOptions: OrderOption[]) => ({
  id: column.name,
  header: i18n.t(column.title),
  enableSorting:
    !!column.orderKey && !!find(orderOptions, (option) => includes(option.name, column.orderKey)),
  size: COLUMNS_SIZE[column.size || 2]
});

const InterestedTable = () => {
  const { t } = useTranslation("activities", { keyPrefix: "show.interested" });

  const {
    selectedIds,
    setSelectedIds,
    filteredWatchers,
    watchersMetadata,
    isWatchersLoading,
    selectedColumns: columns,
    setSelectedOrder
  } = useInterestedContext();

  const renderCell = useCellFactory();

  const { sorting, handleSortingChange } = useTableSorting({
    metaOrder: watchersMetadata?.order,
    setParamsOrder: (updatedOrder) => setSelectedOrder(updatedOrder || {}),
    parseTableSortingId: (id) => find(columns, ["name", id])?.orderKey || id,
    parseMetaOrderName: (name) => find(columns, ["orderKey", name])?.name || name
  });

  const generateColumns = useCallback(
    (columnHelper: ColumnHelper<RoleWatcher>) => [
      ...map(columns, (column) =>
        columnHelper.accessor((row) => row[column.name], {
          ...getColumnConfig(column, watchersMetadata?.order?.options || []),
          cell: renderCell(column)
        })
      )
    ],
    [columns, renderCell, watchersMetadata?.order?.options]
  );

  if ((watchersMetadata?.total === 0 || !watchersMetadata) && !isWatchersLoading) {
    return (
      <div className={css.noItems}>
        <NoProfilesIllustration />
        <div className={css.msg}>{t("noInterestedUsers")}</div>
      </div>
    );
  }

  return (
    <Tile variant="light" paddingVariant="lg">
      <Stack>
        <Table
          tableData={filteredWatchers}
          isLoading={isWatchersLoading}
          generateColumns={generateColumns}
          onSortingChange={handleSortingChange}
          controlledSorting={sorting}
          enableSorting={!!watchersMetadata?.order}
          onRowSelectionChange={setSelectedIds}
          rowSelection={selectedIds}
          getRowId={({ id }) => String(id)}
          enableRowSelection={!!setSelectedIds}
          enableMultiRowSelection
        />
      </Stack>
    </Tile>
  );
};

export default InterestedTable;
